import { useRef, useState } from 'react';
import { A11y, Navigation, Pagination } from 'swiper';
import { Swiper } from 'swiper/react';
import { SwiperSlide } from 'swiper/react';

import { Box, Button, Flex, Heading, IconButton, Text } from '@chakra-ui/react';

import { Card } from '~/components/card';
import { ContentContainer } from '~/components/container';
import { SanityImage } from '~/components/sanity-image';
import { trackEvent } from '~/features/analytics/track-event';
import { ProgramContent } from '~/features/program/models/program-content';
import { useI18n } from '~/hooks/use-i18n';

import { useExperimentalSegmentEvents } from '../hooks';
import { ChevronLeftIcon, ChevronRightIcon, ICON_SIZE, UsersIcon } from '../icons';
import { pluralize } from '../utils';

import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

export type ProgramInstructorsSectionProps = NonNullable<StaticProgramInstructorsSectionProps> & {
  theme?: 'light' | 'dark';
};

export function ProgramInstructorsSection({
  instructors,
  theme = 'light',
  programKey,
}: ProgramInstructorsSectionProps) {
  const isExperimentalTrackEventsEnabled = useExperimentalSegmentEvents('dino')?.variant === 'on';
  const { i18n, isRtl } = useI18n();
  // Add refs for custom navigation
  const prevRef = useRef(null);
  const nextRef = useRef(null);

  // State for mobile expandable list
  const initialInstructorsToShow = 5;
  const [showAllInstructors, setShowAllInstructors] = useState(false);

  const numberOfInstructorsLeft = instructors.length - initialInstructorsToShow;
  const buttonLabel = `See ${numberOfInstructorsLeft} More Instructor${numberOfInstructorsLeft > 1 ? 's' : ''}`;

  function handleSeeMore() {
    setShowAllInstructors(true);
  }

  if (!instructors?.length) return null;

  return (
    <Box as='section'>
      <ContentContainer display='flex' flexDirection='column' gap={8}>
        <Box>
          <Flex flexWrap='wrap' gap={2} justifyContent='space-between' mb={4}>
            <Heading
              as='h3'
              size={{
                base: 'heading-6',
                md: 'heading-5',
              }}
            >
              Program Instructors
            </Heading>
            <Flex alignItems='center' gap={2}>
              <UsersIcon color='supportGray.40' height={ICON_SIZE} width={ICON_SIZE} />
              <Text whiteSpace='nowrap'>{pluralize(instructors.length, 'instructor', 'instructors')}</Text>
            </Flex>
          </Flex>
          <Text>
            Unlike typical professors, our instructors come from Fortune 500 and Global 2000 companies and have
            demonstrated leadership and expertise in their professions:
          </Text>
        </Box>
        <Box
          display={{ base: 'none', md: 'block' }}
          sx={{
            '.swiper .swiper-slide': {
              height: 'auto',
              display: 'flex',
            },
          }}
        >
          <Swiper
            key={i18n.dir()}
            direction='horizontal'
            modules={[A11y, Pagination, Navigation]}
            slidesPerView={3}
            spaceBetween={16}
            navigation={{
              prevEl: prevRef.current,
              nextEl: nextRef.current,
            }}
            onBeforeInit={(swiper) => {
              if (swiper.params.navigation && typeof swiper.params.navigation === 'object') {
                swiper.changeLanguageDirection(i18n.dir());
                swiper.params.navigation.prevEl = prevRef.current;
                swiper.params.navigation.nextEl = nextRef.current;
              }
            }}
            onSlideChange={({ activeIndex }) => {
              if (isExperimentalTrackEventsEnabled) {
                trackEvent({
                  name: 'Product Page CTA Clicked',
                  type: 'click',
                  label: 'instructor_scroll_clicked',
                  value: `slide_${activeIndex}`,
                  additionalAttributes: {
                    product_ids: [programKey],
                  },
                });
              }
            }}
          >
            {instructors.map((instructor) => (
              <SwiperSlide key={instructor._id}>
                <InstructorCard instructor={instructor} theme={theme} />
              </SwiperSlide>
            ))}
          </Swiper>
          {instructors.length > 3 && (
            <Flex gap={2} justifyContent='center' marginTop={8}>
              <IconButton
                ref={prevRef}
                aria-label='Previous'
                size='sm'
                variant='outline'
                icon={
                  isRtl ? (
                    <ChevronRightIcon height='20px' width='20px' />
                  ) : (
                    <ChevronLeftIcon height='20px' width='20px' />
                  )
                }
              />
              <IconButton
                ref={nextRef}
                aria-label='Next'
                size='sm'
                variant='outline'
                icon={
                  isRtl ? (
                    <ChevronLeftIcon height='20px' width='20px' />
                  ) : (
                    <ChevronRightIcon height='20px' width='20px' />
                  )
                }
              />
            </Flex>
          )}
        </Box>
        <Box display={{ base: 'block', md: 'none' }}>
          <Flex flexDirection='column' gap={4}>
            {instructors.map((instructor, index) => (
              <InstructorCard
                key={instructor._id}
                hidden={!showAllInstructors && index >= initialInstructorsToShow}
                instructor={instructor}
                priority={index < 3}
                theme={theme}
              />
            ))}
          </Flex>
          {instructors.length > initialInstructorsToShow && !showAllInstructors && (
            <Button display='block' mt={4} variant='outline' width='100%' onClick={handleSeeMore}>
              {buttonLabel}
            </Button>
          )}
        </Box>
      </ContentContainer>
    </Box>
  );
}

function InstructorCard({
  instructor,
  theme,
  priority = false,
  hidden = false,
}: {
  instructor: ProgramInstructorsSectionProps['instructors'][number];
  theme: 'light' | 'dark';
  priority?: boolean;
  hidden?: boolean;
}) {
  return (
    <Card
      alignItems='center'
      as='article'
      bgColor={theme === 'light' ? '#F9FBFD' : undefined}
      bgGradient={theme === 'dark' ? 'linear(180deg, #0B0C74 0%, #080435 100%)' : undefined}
      border='1px solid'
      borderColor={theme === 'light' ? '#E8E8E8' : 'blue.500'}
      borderRadius='8px'
      color={theme === 'light' ? 'black' : 'white'}
      display='flex'
      flex={1}
      flexDirection={{ base: 'row', md: 'column' }}
      gap={{ base: 4, md: '16px' }}
      hidden={hidden}
      marginLeft={{ base: '24px', md: 0 }}
      marginTop={{ base: 0, md: '64px' }}
      position='relative'
      width={{ base: 'auto', md: '100%' }}
      boxShadow={{
        base: theme === 'dark' ? '0px 0px 25px 0px #2015FF40' : undefined,
        md: theme === 'dark' ? '0px 0px 25px 0px #2015FF40' : 'unset',
      }}
      padding={{
        base: '12px 16px 12px 33px',
        md: '56px 24px 24px',
      }}
    >
      <Box
        bgColor='primary.orange'
        border={{ base: '3px solid white', md: '5px solid white' }}
        borderRadius='50%'
        boxShadow='0px 4px 10px 0px #7C8FBB40'
        flexShrink={0}
        height={{ base: '56px', md: '96px' }}
        marginLeft={{ base: '-56px', md: 0 }}
        marginTop={{ base: 0, md: '-116px' }}
        overflow='hidden'
        position='relative'
        width={{ base: '56px', md: '96px' }}
      >
        {instructor.image && (
          <SanityImage
            alt=''
            image={instructor.image}
            priority={priority}
            quality={50}
            sizes='96px'
            style={{
              objectFit: 'cover',
              pointerEvents: 'none',
              objectPosition: 'center',
            }}
            fill
          />
        )}
      </Box>
      <Box
        alignItems={{ base: 'flex-start', md: 'center' }}
        display='flex'
        flex={{ base: 'none', md: 1 }}
        flexDirection='column'
        gap='4px'
        maxWidth='100%'
        textAlign={{ base: 'start', md: 'center' }}
      >
        <Heading as='p' size='heading-7'>
          {instructor.name}
        </Heading>
        <Text as='p' color='supportGray.70' fontSize='sm' lineHeight='sm'>
          {instructor.jobTitle}
        </Text>
      </Box>
    </Card>
  );
}

export function transformStaticProgramInstructorsSectionProps({ programContent }: { programContent: ProgramContent }) {
  if (!programContent.instructors?.length) {
    return null;
  }

  return {
    instructors: programContent.instructors.map((instructor) => ({
      _id: instructor._id,
      name: instructor.name,
      jobTitle: instructor.jobTitle,
      image: instructor.image,
    })),
    programKey: programContent.key,
  };
}

export type StaticProgramInstructorsSectionProps = ReturnType<typeof transformStaticProgramInstructorsSectionProps>;
